import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";

/* Turn browser notifications on for this device.
 * Deliberately per-device: someone may want alerts on their phone but not on a
 * shared office desktop, so each browser opts in separately. */

function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

const supported = () =>
    typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function PushToggle() {
    const [state, setState] = useState("loading"); // loading | unsupported | off | on | blocked | unavailable
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!supported()) { setState("unsupported"); return; }
            try {
                const { data } = await api.get("/push/config");
                if (cancelled) return;
                if (!data.enabled) { setState("unavailable"); return; }
                window.__vapid = data.public_key;
                if (Notification.permission === "denied") { setState("blocked"); return; }
                const reg = await navigator.serviceWorker.ready;
                const sub = await reg.pushManager.getSubscription();
                if (!cancelled) setState(sub ? "on" : "off");
            } catch {
                if (!cancelled) setState("unavailable");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const enable = async () => {
        setBusy(true);
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                setState(permission === "denied" ? "blocked" : "off");
                toast.error("Notifications weren't allowed for this site.");
                return;
            }
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(window.__vapid),
            });
            await api.post("/push/subscribe", { subscription: sub.toJSON() });
            setState("on");
            toast.success("Notifications are on for this device");
        } catch (e) {
            toast.error("Could not turn on notifications on this device");
        } finally { setBusy(false); }
    };

    const disable = async () => {
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
                await sub.unsubscribe();
            }
            setState("off");
            toast.success("Notifications turned off for this device");
        } catch { toast.error("Could not turn them off"); }
        finally { setBusy(false); }
    };

    const sendTest = async () => {
        try {
            const { data } = await api.post("/push/test");
            if (data.sent) toast.success("Test notification sent — check your notifications");
            else toast.error("No devices are subscribed yet");
        } catch { toast.error("Could not send a test"); }
    };

    if (state === "loading" || state === "unsupported" || state === "unavailable") return null;

    return (
        <div className="rounded-lg border border-border p-3 flex items-start gap-3" data-testid="push-toggle">
            <div className={`mt-0.5 ${state === "on" ? "text-primary" : "text-muted-foreground"}`}>
                {state === "on" ? <BellRing className="size-5" /> : state === "blocked" ? <BellOff className="size-5" /> : <Bell className="size-5" />}
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">Notifications on this device</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                    {state === "on"
                        ? "Approvals, payslips and alerts will reach you even when SAHOD is closed."
                        : state === "blocked"
                            ? "Your browser is blocking notifications for this site. Allow them in the address-bar site settings, then reload."
                            : "Get approvals and payslip alerts without keeping SAHOD open."}
                </p>
                {state !== "blocked" && (
                    <div className="flex gap-2 mt-2">
                        {state === "on" ? (
                            <>
                                <button className="h-8 px-3 rounded-md border border-border text-xs" disabled={busy} onClick={disable} data-testid="push-disable">
                                    Turn off
                                </button>
                                <button className="h-8 px-3 rounded-md bg-secondary text-xs" onClick={sendTest} data-testid="push-test">
                                    Send a test
                                </button>
                            </>
                        ) : (
                            <button className="h-8 px-3 rounded-md bg-primary text-white text-xs font-medium" disabled={busy} onClick={enable} data-testid="push-enable">
                                {busy ? "Enabling…" : "Turn on notifications"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
